import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  Link2,
  Workflow,
  Activity,
  CheckCircle,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { api, endpoints } from '@/lib/api-client';

export const Route = createFileRoute('/_dashboard/overview')({
  component: OverviewPage,
});

interface HealthStatus {
  status: string;
  services?: {
    postgres: string;
    redis: string;
    temporal?: string;
    jackson?: string;
  };
}

interface Stats {
  contacts: number;
  connections: number;
  workflows: number;
  executions: number;
}

function OverviewPage() {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [stats, setStats] = useState<Stats>({
    contacts: 0,
    connections: 0,
    workflows: 0,
    executions: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch health status
        const healthData = await api.get<HealthStatus>(endpoints.healthDetailed);
        setHealth(healthData);

        // Fetch stats (these would come from actual endpoints)
        // For now, using mock data
        setStats({
          contacts: 156,
          connections: 4,
          workflows: 12,
          executions: 847,
        });
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const statCards = [
    {
      title: 'Total Contacts',
      value: stats.contacts,
      icon: Users,
      description: 'Synced from all integrations',
    },
    {
      title: 'Active Connections',
      value: stats.connections,
      icon: Link2,
      description: 'CRM & data integrations',
    },
    {
      title: 'Workflows',
      value: stats.workflows,
      icon: Workflow,
      description: 'Automated processes',
    },
    {
      title: 'Executions',
      value: stats.executions,
      icon: Activity,
      description: 'Last 30 days',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {loading ? '...' : stat.value.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Service Status */}
      <Card>
        <CardHeader>
          <CardTitle>Service Status</CardTitle>
          <CardDescription>Real-time health of platform services</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <ServiceStatus
              name="PostgreSQL"
              status={health?.services?.postgres || 'unknown'}
              loading={loading}
            />
            <ServiceStatus
              name="Redis"
              status={health?.services?.redis || 'unknown'}
              loading={loading}
            />
            <ServiceStatus
              name="Temporal"
              status={health?.services?.temporal || 'unknown'}
              loading={loading}
            />
            <ServiceStatus
              name="Jackson SSO"
              status={health?.services?.jackson || 'unknown'}
              loading={loading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Latest workflow executions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                { name: 'CRM Sync', status: 'completed', time: '2 min ago' },
                { name: 'Lead Enrichment', status: 'completed', time: '15 min ago' },
                { name: 'Email Automation', status: 'running', time: '23 min ago' },
                { name: 'Data Export', status: 'failed', time: '1 hour ago' },
              ].map((activity, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {activity.status === 'completed' && (
                      <CheckCircle className="h-4 w-4 text-emerald-500 dark:text-emerald-600 coral:text-primary" />
                    )}
                    {activity.status === 'running' && (
                      <Clock className="h-4 w-4 text-blue-500 dark:text-blue-400 coral:text-ring animate-pulse" />
                    )}
                    {activity.status === 'failed' && (
                      <AlertCircle className="h-4 w-4 text-red-500 dark:text-red-400 coral:text-destructive" />
                    )}
                    <span className="text-sm">{activity.name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{activity.time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              <QuickActionButton
                href="/integrations"
                label="Add Integration"
                description="Connect a new data source"
              />
              <QuickActionButton
                href="/workflows"
                label="Create Workflow"
                description="Build an automation"
              />
              <QuickActionButton
                href="/api-keys"
                label="Generate API Key"
                description="For programmatic access"
              />
              <QuickActionButton
                href="/settings"
                label="Configure SSO"
                description="Set up enterprise login"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ServiceStatus({
  name,
  status,
  loading,
}: {
  name: string;
  status: string;
  loading: boolean;
}) {
  const getStatusBadge = () => {
    if (loading) return <Badge variant="secondary">Checking...</Badge>;
    switch (status) {
      case 'connected':
      case 'healthy':
        return <Badge className="bg-emerald-500 dark:bg-emerald-600 coral:bg-primary">Connected</Badge>;
      case 'disconnected':
      case 'unhealthy':
        return <Badge variant="destructive">Disconnected</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border">
      <span className="text-sm font-medium">{name}</span>
      {getStatusBadge()}
    </div>
  );
}

function QuickActionButton({
  href,
  label,
  description,
}: {
  href: string;
  label: string;
  description: string;
}) {
  return (
    <Link
      to={href}
      className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent transition-colors"
    >
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <span className="text-muted-foreground">→</span>
    </Link>
  );
}
